import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES, isVideo } from "../lib/site";
import { Flag, Heart, Medal, Users, Compass } from "lucide-react";

const SUBNAV = [
  { label: "Histoire", hash: "#histoire" },
  { label: "Le bureau", hash: "#bureau" },
  { label: "Formation", hash: "#formation" },
  { label: "Nos valeurs", hash: "#valeurs" },
  { label: "Projet sportif", hash: "#projet-sportif" },
  { label: "Haut Niveau", hash: "#haut-niveau" },
  { label: "Savoir Rouler & Sénégal", hash: "#savoir-rouler" },
];

const VALUES = [
  { icon: Heart, title: "Passion", text: "Le vélo avant tout : le plaisir de rouler, de s'entraîner et de courir ensemble." },
  { icon: Users, title: "Collectif", text: "Un club est une équipe. On progresse ensemble, on gagne ensemble, on perd ensemble." },
  { icon: Medal, title: "Exigence", text: "L'ambition sportive, dans la préparation comme dans le comportement, à tous les niveaux." },
  { icon: Compass, title: "Ouverture", text: "Du Boulou au Sénégal : le club croit au cyclisme comme vecteur de lien et de partage." },
  { icon: Flag, title: "Transmission", text: "Former les jeunes, transmettre la culture du vélo et les valeurs du sport." },
];

function ClubSection({ id, number, title, children }) {
  return (
    <section id={id} data-testid={`club-section-${id}`} className="py-20 sm:py-28 scroll-mt-28 border-b border-navy/10 last:border-0">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-heading text-sm tracking-[0.3em] text-rouge font-semibold">{number}</span>
            <span className="h-px w-12 bg-rouge" />
          </div>
          <h2 className="font-heading uppercase font-semibold tracking-tighter text-navy text-3xl sm:text-5xl">{title}</h2>
        </Reveal>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

export default function Club() {
  return (
    <>
      <SEO title="Le Club" description="Histoire, bureau, formation et valeurs du Club Cycliste Le Boulou, club de cyclisme des Pyrénées-Orientales." />
      <PageHeader kicker="Le Club" title="Notre ADN" />

      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur-xl border-b border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto">
          {SUBNAV.map((s) => (
            <a
              key={s.label}
              href={`/le-club${s.hash}`}
              data-testid={`club-subnav-${s.hash.slice(1)}`}
              className="shrink-0 px-4 py-4 font-heading uppercase tracking-wider text-xs text-navy/70 hover:text-rouge transition-colors duration-300"
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      <ClubSection id="histoire" number="01" title="Histoire">
        <Reveal>
          <p className="text-navy/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            Depuis 2003, le Club Cycliste Le Boulou construit son histoire autour d'une même passion : le vélo.
            Plus de 20 ans de passion, de transmission et de projets.
          </p>
          <Link
            to="/histoire"
            data-testid="club-histoire-link"
            className="mt-8 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
          >
            Découvrir notre histoire
          </Link>
        </Reveal>
      </ClubSection>

      <ClubSection id="bureau" number="02" title="Le bureau du club">
        <Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="club-bureau">
            {[
              ["Président", "Stéphane Roger"],
              ["Trésorière", "Maria D'Ambrosio"],
              ["Secrétaire", "Gabriel Roger"],
              ["Responsable FSGT", "Didier Respaut"],
              ["Responsable équipe féminine", "Stéphane Roger"],
              ["Responsable équipe homme", "Valentin Abadie"],
              ["Responsable loisir", "Aurélien Cauquil"],
              ["Responsable loisir femme", "Maria D'Ambrosio"],
              ["Responsable école de vélo", "Stéphane Roger"],
            ].map(([role, nom]) => (
              <div key={role} className="border border-navy/10 bg-white px-6 py-5 hover:border-rouge transition-colors duration-300" data-testid={`bureau-${role.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <p className="font-heading uppercase tracking-wider text-[11px] text-steel">{role}</p>
                <p className="mt-1.5 font-heading uppercase font-semibold text-navy text-lg">{nom}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </ClubSection>

      <ClubSection id="formation" number="03" title="Formation">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="overflow-hidden group order-2 lg:order-1">
              {isVideo(IMAGES.ecole) ? (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{ objectPosition: "center 45%" }}
                  className="w-full h-[340px] object-cover transition-transform duration-700 group-hover:scale-105"
                >
                  <source src={IMAGES.ecole} type="video/mp4" />
                  <source src={IMAGES.ecole.replace(/\.mp4$/i, ".webm")} type="video/webm" />
                </video>
              ) : (
                <img
                  src={IMAGES.ecole}
                  alt="Les jeunes de l'école de vélo du Club Cycliste Le Boulou"
                  loading="lazy"
                  style={{ objectPosition: "center 45%" }}
                  className="w-full h-[340px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
              )}
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-navy/80 text-base sm:text-lg leading-relaxed">
                La formation des jeunes est un pilier du club. De l'apprentissage des fondamentaux à la découverte de la
                compétition, le club accompagne chaque jeune dans sa progression, avec un encadrement attentif et des
                valeurs fortes : sécurité, respect, plaisir et esprit d'équipe.
              </p>
              <p className="mt-5 text-steel leading-relaxed" data-testid="formation-srav-text">
                Le club intervient également dans les écoles primaires du territoire avec le dispositif
                « Savoir Rouler à Vélo » : apprendre à chaque enfant à maîtriser son vélo, à circuler en sécurité et à
                gagner en autonomie avant l'entrée au collège.
              </p>
            </div>
          </div>
        </Reveal>
      </ClubSection>

      <ClubSection id="valeurs" number="04" title="Nos valeurs">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="group bg-white border border-navy/10 p-8 h-full hover:border-rouge transition-[border-color] duration-300">
                <v.icon size={26} className="text-rouge transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-5 font-heading uppercase font-semibold tracking-tight text-navy text-2xl">{v.title}</h3>
                <p className="mt-3 text-sm text-steel leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </ClubSection>

      <ClubSection id="projet-sportif" number="05" title="Projet sportif">
        <Reveal>
          <p className="text-navy/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            Le projet sportif du club repose sur quatre axes complémentaires : la performance, la formation, le
            développement du vélo sous toutes ses formes et l'ancrage territorial.
          </p>
          <Link
            to="/projets/projet-sportif"
            data-testid="club-projet-sportif-link"
            className="mt-8 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
          >
            Découvrir le projet sportif
          </Link>
        </Reveal>
      </ClubSection>

      <ClubSection id="haut-niveau" number="06" title="Haut Niveau">
        <Reveal>
          <p className="text-navy/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            De l'équipe féminine N1/UCI à Expresso Racing engagée en Nationale 3, le club structure ses équipes de
            compétition pour performer au niveau national et faire progresser ses coureurs et coureuses.
          </p>
          <Link
            to="/equipes"
            data-testid="club-haut-niveau-link"
            className="mt-8 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
          >
            Découvrir nos équipes
          </Link>
        </Reveal>
      </ClubSection>

      <ClubSection id="savoir-rouler" number="07" title="Savoir Rouler & Sénégal">
        <Reveal>
          <p className="text-navy/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            Des écoles primaires du territoire au Sénégal, le club fait vivre le vélo comme un vecteur
            d'apprentissage, de lien et de partage.
          </p>
          <Link
            to="/projets/senegal"
            data-testid="club-savoir-rouler-link"
            className="mt-8 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
          >
            Découvrir ces projets
          </Link>
        </Reveal>
      </ClubSection>
    </>
  );
}
